import React, {
  forwardRef,
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';
import { ContentHeader, Table, } from '@backstage/core-components';
import SearchIcon from '@material-ui/icons/Search';
import ClearIcon from '@material-ui/icons/Clear';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';
import ArrowDropDown from '@material-ui/icons/ArrowDropDown';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import FirstPageIcon from '@material-ui/icons/FirstPage';
import LastPageIcon from '@material-ui/icons/LastPage';
import ArrowDownwardIcon from '@material-ui/icons/ArrowDownward';
import useStyles, { StyledBoxContentHeader } from './style';
import { Box } from '@material-ui/core';
import { Filter, } from './Filter';
import TableToolbar from './TableToolbar';

import { Pagination } from './Pagination';

















const defaultInitialState = {
  search: '',
  filtersOpen: false,
  filters: {},
};

function extractValueByField(data, fields) {
  const path = fields.split('.');
  let value = data[path[0]];
  for (let i = 1; i < path.length; ++i) {
    if (!value) {
      return value;
    }
    const field = path[i];
    value = value[field];
  }
  return value;
}

function TableComponent(props) {
  const {
    dense,
    emptyContent,
    options,
    icons,
    isLoading,
    tableName,
    tableHeaderButton,
    components,
    filters,
    initialState,
    data,
    keepDetailExpandedOnRefresh,
    onStateChange,
    onRefresh,
    columns,
    ...extraProps
  } = props;
  const tableRef = useRef();
  const openedPanels = useRef({});
  const oldFunction = useRef

(null);
  const calculatedInitialState = { ...defaultInitialState, ...initialState };
  const [selectedFiltersLength, setSelectedFiltersLength] = useState(0);
  const [tableData, setTableData] = useState(data);
  const [filtersOpen, setFiltersOpen] = useState(
    calculatedInitialState.filtersOpen,
  );
  const [selectedFilters, setSelectedFilters] = useState(
    calculatedInitialState.filters,
  );
  const [_search, setSearch] = useState(calculatedInitialState.search);

  const hasFilters = !!(filters && filters.length > 0);
  const showFilters =
    hasFilters && filtersOpen && data && typeof data !== 'function';

  //  This is a customized logic that keeps the detail panel open
  //  even when the data in the table is refreshed.
  useEffect(() => {
    if (keepDetailExpandedOnRefresh) {
      if (!oldFunction.current) {
        oldFunction.current = tableRef.current?.onToggleDetailPanel;
      }

      if (oldFunction.current === tableRef.current?.onToggleDetailPanel) {
        tableRef.current.onToggleDetailPanel = (
          path,
          render,
        ) => {
          if (tableRef.current.props.data[path[0]].tableData.showDetailPanel) {
            delete openedPanels.current[path[0]];
          } else {
            openedPanels.current = {
              ...openedPanels.current,
              [path[0]]: true,
            };
          }

          oldFunction.current(path, render);
        };
      }
    }
  }, [tableRef, keepDetailExpandedOnRefresh]);

  const toggleFilters = useCallback(
    () => setFiltersOpen(isOpen => !isOpen),
    [setFiltersOpen],
  );

  const Toolbar = useCallback(
    toolbarProps => {
      return (
        React.createElement(TableToolbar, {
          toggleFilters: toggleFilters,
          hasFilters: hasFilters,
          selectedFiltersLength: selectedFiltersLength,
          setSearch: setSearch,
          onRefresh: onRefresh,
          ...toolbarProps,}
        )
      );
    },
    [toggleFilters, hasFilters, selectedFiltersLength, setSearch, onRefresh],
  );

  const getFieldByTitle = useCallback(
    titleValue => {
      const column = columns.find(el => el.title === titleValue);
      return column?.field;
    },
    [columns],
  );

  useEffect(() => {
    if (typeof data === 'function') {
      return;
    }

    if (!selectedFilters) {
      setTableData(data);
      return;
    }

    const selectedFiltersArray = Object.values(selectedFilters);

    if (data && selectedFiltersArray.flat().length) {
      const newData = data.filter(
        item =>
          !!Object.entries(selectedFilters)
            .filter(([, value]) => !!value.length)
            .every(([key, filterValue]) => {
              if (
                item &&
                typeof item === 'object' &&
                typeof key === 'string' &&
                key in item
              ) {
                const value = (item )[key];
                return !!value;
              }
              const fieldValue = extractValueByField(
                item,
                getFieldByTitle(key),
              );
              if (Array.isArray(fieldValue) && Array.isArray(filterValue)) {
                return fieldValue.some(v => filterValue.includes(v));
              } else if (Array.isArray(fieldValue)) {
                return fieldValue.includes(filterValue);
              } else if (Array.isArray(filterValue)) {
                return filterValue.includes(fieldValue);
              }
              return fieldValue === filterValue;
            }),
      );
      setTableData(newData);
    } else {
      setTableData(data);
    }
    setSelectedFiltersLength(selectedFiltersArray.flat().length);
  }, [data, selectedFilters, getFieldByTitle]);

  const classes = useStyles();

  const constructFilters = (filterConfig, dataValue) => {
    const extractDistinctValues = (field) => {
      const distinctValues = new Set();
      const addValue = (value) => {
        if (value) {
          distinctValues.add(value);
        }
      };

      if (dataValue) {
        dataValue.forEach(item => {
          if (
            item &&
            typeof item === 'object' &&
            typeof field === 'string' &&
            field in item
          ) {
            const value = (item )[field];
            addValue(value);
            return;
          }
          const value = extractValueByField(item, getFieldByTitle(field));
          if (Array.isArray(value)) {
            value.forEach(addValue);
          } else {
            addValue(value);
          }
        });
      }
      return distinctValues;
    };

    const constructSelect = (filter) => {
      return {
        placeholder: 'All results',
        label: filter.column,
        multiple: filter.type === 'multiple-select',
        items: [...extractDistinctValues(filter.column)].sort().map(value => ({
          label: value,
          value,
        })),
      };
    };

    return filterConfig.map(filter => ({
      ...filter,
      element: constructSelect(filter),
    }));
  };

  return (
    React.createElement(Box, { className: classes.container,}
      , (tableName || tableHeaderButton) && (
        React.createElement(StyledBoxContentHeader, null
          , React.createElement(ContentHeader, { title: tableName,}, tableHeaderButton)
        )
      )
      , React.createElement(Box, { display: "flex",}
        , showFilters && (
          React.createElement(Filter, {
            filters: constructFilters(filters, data),
            onChangeFilters: setSelectedFilters,
            selectedFilters: selectedFilters,}
          )
        )
        , React.createElement(Table, {
          tableRef: tableRef,
          data: 
            (tableData )?.map((d, i) => ({
              ...d,
              tableData: {
                showDetailPanel: openedPanels.current[i]
                  ? props.detailPanel
                  : null,
              },
            })) || []
          ,
          columns: columns,
          emptyContent: emptyContent,
          options: {
            maxBodyHeight: 'calc(100vh - 200px)',
            actionsColumnIndex: -1,
            pageSize: 10,
            padding: dense ? 'dense' : 'default',
            sorting: true,
            paging: true,
            detailPanelColumnAlignment: 'right',
            ...options,
          },
          localization: { toolbar: { searchPlaceholder: 'Search' } },
          icons: {
            Search: forwardRef((forwardProps, ref) => (
              React.createElement(SearchIcon, { ...forwardProps, ref: ref,} )
            )),
            ResetSearch: forwardRef((forwardProps, ref) => (
              React.createElement(ClearIcon, { ...forwardProps, ref: ref,} )
            )),
            NextPage: forwardRef((forwardProps, ref) => (
              React.createElement(ChevronRightIcon, { ...forwardProps, ref: ref,} )
            )),
            PreviousPage: forwardRef((forwardProps, ref) => (
              React.createElement(ChevronLeftIcon, { ...forwardProps, ref: ref,} )
            )),
            FirstPage: forwardRef((forwardProps, ref) => (
              React.createElement(FirstPageIcon, { ...forwardProps, ref: ref,} )
            )),
            LastPage: forwardRef((forwardProps, ref) => (
              React.createElement(LastPageIcon, { ...forwardProps, ref: ref,} )
            )),
            DetailPanel: forwardRef((forwardProps, ref) => (
              React.createElement(ArrowDropDown, { ...forwardProps, ref: ref,} )
            )),
            SortArrow: forwardRef((forwardProps, ref) => (
              React.createElement(ArrowDownwardIcon, { ...forwardProps, ref: ref,} )
            )),
            ...icons,
          },
          isLoading: isLoading,
          components: {
            Toolbar,
            Pagination,
            ...components,
          },
          ...extraProps,}
        )
      )
    )
  );
}

export default TableComponent;
